/**
 * Paths that belong to the server, not to this app.
 *
 * `/mcp/*` is the MCP server (an OAuth authorization server plus a JSON-RPC
 * endpoint) running as a Netlify function. `/.well-known/*` is its discovery
 * metadata. `/.netlify/*` is the function runtime itself. The React app has no
 * route for any of them and must never render on one.
 *
 * It did, though, and the failure was invisible. The PWA service worker takes
 * scope '/' with an SPA navigation fallback, so it answered *every* navigation
 * from cache — including GitHub's redirect back to
 * `/mcp/callback?code=...&state=...`. The request never reached the function.
 * The app booted instead, saw OAuth parameters in the URL, assumed they were
 * its own sign-in, and rejected the sealed state as a CSRF attack. Nothing
 * appeared in the server logs because nothing arrived; curl and incognito
 * always passed because neither has a service worker.
 *
 * The real fix is `navigateFallbackDenylist` in vite.config.ts. This module is
 * the escape hatch for browsers that already installed the old worker: with
 * `registerType: 'prompt'` a stale worker keeps control until the user accepts
 * an update, and a user stuck in the broken connector flow has no reason to
 * think "unregister a service worker" is the next step.
 */

const SERVER_PATHS = [
  /^\/mcp(?:\/|$)/,
  /^\/\.well-known\//,
  /^\/\.netlify\//,
];

/** True when this path is the server's, not a route of this app. */
export function isServerPath(pathname: string): boolean {
  return SERVER_PATHS.some((pattern) => pattern.test(pathname));
}

/**
 * What boot should do for a given path.
 *
 * - `render`  — normal app route, mount React.
 * - `reclaim` — a stale worker served the app here; drop it and reload so the
 *   request reaches the server.
 * - `explain` — we already reclaimed once and are *still* here, so reloading
 *   again would loop. Say what happened instead of spinning.
 */
export type BootAction = 'render' | 'reclaim' | 'explain';

export function bootAction(pathname: string, alreadyReclaimed: boolean): BootAction {
  if (!isServerPath(pathname)) return 'render';
  return alreadyReclaimed ? 'explain' : 'reclaim';
}
